import React, { FunctionComponent } from 'react';
import { withRouter, match, Switch } from 'react-router-dom';
import { map, addIndex, path, pathOr } from 'ramda';
import { compose } from 'recompose';
import withStyles from 'react-jss';
import { Grid } from 'semantic-ui-react';

import TabbedMenu from 'components/TabbedMenu';
import { Tabs } from 'components/TabbedMenu/TabbedMenu.helper';
import { MerchantDetailsPlaceholder } from 'components/Placeholder';
import ProtectedRoute from 'components/ProtectedRoute';
import { QueryResponse } from 'components/Header/components/SecondaryNav/components/ExternalSubNav/ExternalSubNav';
import Query from 'components/Query';
import { ClassProp } from 'helpers/types';
import MerchantVenuesOnboarding from 'scenes/Home/scenes/MerchantVenuesOnboarding';
import Tier3VenueOnboarding from 'scenes/Tier3VenueOnboarding';
import AccountManagement from 'scenes/AccountManagement';

import MerchantOverview from '../sections/MerchantOverview';
import MerchantVenues from '../sections/MerchantVenues';
import MerchantOffers from '../sections/MerchantOffers';
import MerchantNetworkHealth from '../sections/MerchantNetworkHealth';
import MerchantDetails from '../sections/MerchantDetails';
import MerchantVenueDetails from '../sections/MerchantVenueDetails';
import MerchantLift from '../sections/MerchantLift';
import MerchantBrandAssets from '../sections/MerchantBrandAssets';
import MerchantAudience from '../sections/MerchantAudience';
import PulseOffer from '../sections/MerchantOffers/scenes/PulseOffer';
import { GET_MERCHANT } from '../sections/MerchantDetails/MerchantDetails.queries';
import { Merchant } from '../sections/MerchantDetails/MerchantDetails';
import MerchantCampaigns from '../sections/MerchantCampaigns';

type Params = {
  history: History;
  location: Location;
  match: match;
};

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  match: match;
};

const styles = {
  grid: {
    margin: '1rem 0 0 0 !important',
  },
};

const tabItems: Tabs[] = [
  {
    label: 'Overview',
    path: '/overview/:merchantName',
    key: 'overview',
    component: MerchantOverview,
  },
  {
    label: 'Lift',
    path: '/lift/:merchantName',
    key: 'lift',
    component: MerchantLift,
  },
  {
    label: 'Network Health',
    path: '/network-health/:merchantName',
    key: 'network-health',
    component: MerchantNetworkHealth,
  },
  {
    label: 'Venues',
    path: '/venues/:merchantName',
    key: 'venues',
    component: MerchantVenues,
    exact: true,
  },
  {
    label: 'Campaigns',
    path: '/campaigns/:merchantName',
    key: 'campaigns',
    component: MerchantCampaigns,
    exact: true,
  },
  {
    label: 'Offers',
    path: '/offers/:merchantName',
    key: 'offers',
    component: MerchantOffers,
    exact: true,
  },
  {
    label: 'Offers',
    path: '/offers/:merchantName/offer/:offerUuid',
    key: 'offers',
    component: PulseOffer,
    exact: true,
    hideItem: true,
  },
  {
    label: 'Account Management',
    path: '/account-management/:merchantName',
    key: 'accountManagement',
    component: AccountManagement,
  },
  {
    label: 'Brand Assets',
    path: '/assets/:merchantName',
    key: 'merchant-assets',
    component: MerchantBrandAssets,
  },
  {
    label: 'Audience',
    path: '/audience/:merchantName',
    key: 'audience',
    component: MerchantAudience,
  },
  {
    label: 'Venue',
    path: '/venues/:merchantName/venue/:venueId',
    key: 'venue-details',
    component: MerchantVenueDetails,
    hideItem: true,
    exact: true,
  },
  {
    label: 'Manage Venues',
    path: '/venues/:merchantName/management',
    key: 'venues-management',
    component: MerchantVenuesOnboarding,
    hideItem: true,
    exact: true,
  },
  {
    label: 'Manage Venues',
    path: '/venues/:merchantName/onboarding',
    key: 'venues-onboard',
    component: Tier3VenueOnboarding,
    hideItem: true,
    exact: true,
  },
];

const mapIndexed = addIndex<Tabs>(map);

const renderComponent = (Component: any, merchant: Merchant) => (
  props: Params,
) => <Component {...props} merchant={merchant} />;

const renderRoutes = (match: match, merchant: Merchant) => (
  { path, key, component, exact }: Tabs,
  idx: number,
) => (
  <ProtectedRoute
    path={`${match.path}${path}`}
    component={renderComponent(component, merchant) as FunctionComponent}
    key={`${key}_${idx}`}
    exact={exact}
  />
);

const renderMerchantSuccessLandingPage = ({ classes, match }: Props) => (
  response: QueryResponse,
) => {
  const { data, loading } = response;

  if (loading) {
    return <MerchantDetailsPlaceholder />;
  }

  const merchant = pathOr([], ['merchantList', 'result', 0], data) as Merchant;

  const { merchantName, hasAccountDetails } = merchant;

  return (
    <>
      <MerchantDetails merchant={merchant} />

      <TabbedMenu
        tabItems={tabItems}
        url={match.url}
        merchantName={merchantName}
        hasAccountDetails={hasAccountDetails}
      />

      <Grid columns="equal" className={classes.grid}>
        <Switch>{mapIndexed(renderRoutes(match, merchant), tabItems)}</Switch>
      </Grid>
    </>
  );
};

const MerchantSuccessLandingPage: FunctionComponent<Props> = (props) => (
  <Query
    enableProgressInfo
    query={GET_MERCHANT}
    render={renderMerchantSuccessLandingPage(props)}
    variables={{
      params: {
        merchantId: path(['params', 'merchantId'], props.match),
      },
      package: 'DEFAULT_BRANDING',
    }}
  />
);

export default compose<Props, any>(
  withStyles(styles),
  withRouter,
)(MerchantSuccessLandingPage);
